// Lee un informe exportado y devuelve los datos que contiene.
//
// El informe se escribió para que lo lea una persona, pero está lo bastante
// estructurado como para leerlo de vuelta, y eso lo convierte en la copia de
// seguridad que la app no tenía: quien exporta se lleva de verdad sus datos, no
// una foto de ellos.
//
// Lo que sale de aquí no se escribe en ningún sitio: eso lo decide
// storage/import.js, después de que el usuario vea qué se ha encontrado.

const MESES_CORTOS = { ene: 0, feb: 1, mar: 2, abr: 3, may: 4, jun: 5, jul: 6, ago: 7, sep: 8, oct: 9, nov: 10, dic: 11 };
const MESES_LARGOS = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];
const DIAS_SEMANA = ["lunes", "martes", "miércoles", "jueves", "viernes", "sábado", "domingo"];

// El informe está escrito en español: 70.081 son setenta mil y 52,5 es cincuenta
// y dos y medio. parseFloat a secas devuelve 70 y 52.
function numero(texto) {
  if (!texto) return 0;
  const limpio = String(texto).replace(/[^\d,.-]/g, "").replace(/\./g, "").replace(",", ".");
  const valor = parseFloat(limpio);
  return Number.isFinite(valor) ? valor : 0;
}

function fechaCorta(texto) {
  const m = String(texto).match(/(\d{2})\/(\d{2})\/(\d{2,4})/);
  if (!m) return null;
  const anio = m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3]);
  return new Date(Date.UTC(anio, Number(m[2]) - 1, Number(m[1]))).toISOString();
}

function fechaLarga(texto) {
  const m = String(texto).match(/(\d{1,2})\s+([a-záéíóúñ]+)\.?\s+(\d{4})/i);
  if (!m) return null;
  const clave = m[2].toLowerCase().slice(0, 3);
  const mes = MESES_CORTOS[clave];
  if (mes === undefined) return null;
  return new Date(Date.UTC(Number(m[3]), mes, Number(m[1]))).toISOString();
}

const texto = (nodo) => (nodo ? nodo.textContent.replace(/\s+/g, " ").trim() : "");

// Los identificadores de la rutina se generan de nuevo: los del informe no
// existen, y de todas formas el progreso se vuelve a atar por nombre.
function creadorDeIds() {
  let n = 0;
  const sello = Date.now();
  return () => `imp-${sello}-${n++}`;
}

const METRICAS_CONOCIDAS = {
  "peso corporal": "weight", "grasa corporal": "bodyfat", "ingesta calórica": "calories",
  cuello: "neck", hombros: "shoulders", pecho: "chest",
  "bíceps izquierdo": "bicepL", "bíceps derecho": "bicepR",
  "antebrazo izquierdo": "forearmL", "antebrazo derecho": "forearmR",
  cintura: "waist", caderas: "hips",
  "muslo izquierdo": "thighL", "muslo derecho": "thighR",
  "pantorrilla izquierda": "calfL", "pantorrilla derecha": "calfR",
};

export function parseReport(html) {
  const doc = new DOMParser().parseFromString(html, "text/html");
  if (!doc.querySelector(".kpis")) {
    throw new Error("Ese archivo no es un informe de TRYAD.");
  }

  const nuevoId = creadorDeIds();
  const avisos = [];

  // ── Rutina ────────────────────────────────────────────────────────────────
  const routine = [];
  doc.querySelectorAll("#rutina .card.day").forEach((tarjeta) => {
    const nombre = texto(tarjeta.querySelector(".card-head h3"));
    const exercises = [];
    tarjeta.querySelectorAll("tbody tr").forEach((fila) => {
      const celdas = fila.querySelectorAll("td");
      if (celdas.length < 4) return;
      exercises.push({
        id: nuevoId(),
        name: texto(celdas[0]),
        muscleGroup: texto(celdas[1]) === "—" ? "" : texto(celdas[1]),
        sets: Math.max(1, Math.round(numero(texto(celdas[2])))),
        reps: texto(celdas[3]) === "—" ? "" : texto(celdas[3]),
      });
    });
    const numeroDia = nombre.match(/D[ií]a\s*(\d+)/i);
    routine.push({
      name: nombre,
      short: numeroDia ? `D${numeroDia[1]}` : `D${routine.length + 1}`,
      exercises,
    });
  });

  // ── Ciclos ────────────────────────────────────────────────────────────────
  // El que lleva la etiqueta "en curso" no es historial: es el progreso de la
  // semana que se está entrenando, y vuelve a la rutina, no al archivo.
  const weeks = [];
  const progress = {};

  doc.querySelectorAll("#ciclos .card.cycle").forEach((tarjeta) => {
    const cabecera = texto(tarjeta.querySelector(".card-head h3"));
    const semana = Math.round(numero((cabecera.match(/Semana\s*(\d+)/i) || [])[1] || 0));
    const enCurso = !!tarjeta.querySelector(".tag");
    const completado = fechaLarga(texto(tarjeta.querySelector(".card-head .pill")));

    const days = [];
    tarjeta.querySelectorAll(".cycle-day").forEach((bloque) => {
      const nombreDia = texto(bloque.querySelector("h4"));
      const exercises = [];
      bloque.querySelectorAll("tbody tr").forEach((fila) => {
        const nombre = texto(fila.querySelector(".ex-name"));
        const grupo = texto(fila.querySelector(".ex-group"));
        const sets = [];
        fila.querySelectorAll(".set").forEach((etiqueta) => {
          const [peso, reps] = texto(etiqueta).split("×");
          sets.push({ weight: numero(peso), reps: Math.round(numero(reps)) });
        });
        if (sets.length > 0) exercises.push({ name: nombre, muscleGroup: grupo, sets });
      });
      if (exercises.length > 0) {
        const numeroDia = nombreDia.match(/D[ií]a\s*(\d+)/i);
        days.push({ name: nombreDia, short: numeroDia ? `D${numeroDia[1]}` : nombreDia, exercises });
      }
    });

    if (enCurso) {
      days.forEach((dia) => {
        const indiceDia = routine.findIndex((d) => d.name === dia.name);
        if (indiceDia < 0) {
          avisos.push(`El día "${dia.name}" del ciclo en curso no está en la rutina: sus series no se han recuperado.`);
          return;
        }
        dia.exercises.forEach((ejercicio) => {
          // Se vuelve a atar por nombre, que es lo único que comparten el
          // informe y la rutina. La variante elegida se guardó con su nombre
          // resuelto, así que puede no coincidir con el del ejercicio base.
          const enRutina =
            routine[indiceDia].exercises.find((e) => e.name === ejercicio.name) ||
            routine[indiceDia].exercises.find((e) => ejercicio.name.startsWith(e.name));
          if (!enRutina) {
            avisos.push(`"${ejercicio.name}" ya no está en la rutina: sus series del ciclo en curso no se han recuperado.`);
            return;
          }
          ejercicio.sets.forEach((serie, indice) => {
            progress[`${indiceDia}-${enRutina.id}-${indice}`] = {
              done: true,
              weight: serie.weight,
              reps: serie.reps,
            };
          });
        });
      });
      return;
    }

    const totalSets = days.reduce((a, d) => a + d.exercises.reduce((x, e) => x + e.sets.length, 0), 0);
    const totalVolume = days.reduce(
      (a, d) => a + d.exercises.reduce((x, e) => x + e.sets.reduce((v, s) => v + s.weight * s.reps, 0), 0),
      0
    );

    weeks.push({
      week: semana,
      startedAt: null,
      completedAt: completado,
      days,
      volumeByGroup: {},
      totalSets,
      totalVolume: Math.round(totalVolume),
    });
  });

  weeks.sort((a, b) => (a.week || 0) - (b.week || 0));

  // ── Historial de pesos ────────────────────────────────────────────────────
  // Se reconstruye con el peso más alto de cada ejercicio en cada ciclo
  // cerrado, fechado el día en que se cerró. Es dato real, aunque puede tener
  // menos puntos que el original: el informe no imprime el registro completo.
  const history = {};
  weeks.forEach((ciclo) => {
    if (!ciclo.completedAt) return;
    ciclo.days.forEach((dia) => {
      dia.exercises.forEach((ejercicio) => {
        const maximo = Math.max(0, ...ejercicio.sets.map((s) => s.weight));
        if (maximo <= 0) return;
        if (!history[ejercicio.name]) history[ejercicio.name] = [];
        history[ejercicio.name].push({ date: ciclo.completedAt, weight: maximo });
      });
    });
  });
  Object.keys(history).forEach((nombre) => {
    history[nombre] = history[nombre].sort((a, b) => a.date.localeCompare(b.date)).slice(-50);
  });

  // ── Medidas ───────────────────────────────────────────────────────────────
  const measurements = {};
  const customMetrics = [];
  doc.querySelectorAll("#medidas .metric").forEach((bloque) => {
    const nombre = texto(bloque.querySelector("h4"));
    let id = METRICAS_CONOCIDAS[nombre.toLowerCase()];
    if (!id) {
      id = `custom_imp_${customMetrics.length}`;
      const primera = bloque.querySelector(".reading");
      const unidad = primera ? texto(primera).split(" ").pop() : "cm";
      customMetrics.push({ id, name: nombre, unit: unidad, icon: "body", custom: true });
    }
    const lecturas = [];
    bloque.querySelectorAll(".reading").forEach((lectura) => {
      const etiquetaFecha = texto(lectura.querySelector("em"));
      const fecha = fechaCorta(etiquetaFecha);
      if (!fecha) return;
      lecturas.push({ date: fecha, value: numero(texto(lectura).replace(etiquetaFecha, "")) });
    });
    if (lecturas.length > 0) measurements[id] = lecturas;
  });

  // ── Días entrenados ───────────────────────────────────────────────────────
  const trainingDates = [];
  doc.querySelectorAll("#calendario .month").forEach((bloque) => {
    const cabecera = texto(bloque.querySelector(".card-head h4")).toLowerCase();
    const mes = MESES_LARGOS.findIndex((m) => cabecera.startsWith(m));
    const anio = Number((cabecera.match(/(\d{4})/) || [])[1]);
    if (mes < 0 || !anio) return;
    bloque.querySelectorAll(".cell.on").forEach((celda) => {
      const dia = Number(texto(celda));
      if (dia) trainingDates.push(`${anio}-${String(mes + 1).padStart(2, "0")}-${String(dia).padStart(2, "0")}`);
    });
  });
  trainingDates.sort();

  // ── Dieta ─────────────────────────────────────────────────────────────────
  const filasPorComida = new Map();
  doc.querySelectorAll("#dieta .diet-day").forEach((bloque) => {
    const dia = DIAS_SEMANA.indexOf(texto(bloque.querySelector("h4")).toLowerCase());
    if (dia < 0) return;
    bloque.querySelectorAll("tbody tr").forEach((fila) => {
      const nombre = texto(fila.querySelector(".meal-name"));
      if (!nombre) return;
      if (!filasPorComida.has(nombre)) filasPorComida.set(nombre, Array(7).fill(""));
      filasPorComida.get(nombre)[dia] = texto(fila.querySelector("td"));
    });
  });

  let diet = null;
  if (filasPorComida.size > 0) {
    const meals = [...filasPorComida.keys()].map((nombre, i) => ({ id: `meal_imp_${i}`, name: nombre }));
    const plan = {};
    const filas = [...filasPorComida.values()];
    meals.forEach((meal, i) => { plan[meal.id] = filas[i]; });
    diet = { meals, plan, note: texto(doc.querySelector("#dieta .diet-note")) };
  }

  // ── Cabecera ──────────────────────────────────────────────────────────────
  const userName = texto(doc.querySelector(".cover h1 .hl"));
  const subtituloRutina = texto(doc.querySelector("#rutina .section-head p"));
  const week = Math.round(numero((subtituloRutina.match(/Semana\s*(\d+)/i) || [])[1] || weeks.length + 1));
  const objetivo = Math.round(numero((texto(doc.querySelector(".kpis")).match(/Objetivo\s*([\d.]+)/i) || [])[1] || 0));
  const generado = texto(doc.querySelector("footer"));

  if (routine.length === 0 && weeks.length === 0) {
    throw new Error("El informe no contiene ni rutina ni ciclos entrenados.");
  }

  return {
    userName,
    week,
    routine,
    progress,
    weeks,
    history,
    measurements,
    customMetrics,
    trainingDates,
    weeklyGoal: objetivo > 0 ? objetivo : null,
    diet,
    generado,
    avisos,
  };
}

// Lo que se le enseña al usuario antes de tocar nada.
export function describeReport(datos) {
  return [
    ["Nombre", datos.userName || "sin nombre"],
    ["Días de rutina", datos.routine.length],
    ["Ejercicios", datos.routine.reduce((a, d) => a + d.exercises.length, 0)],
    ["Ciclos cerrados", datos.weeks.length],
    ["Series registradas", datos.weeks.reduce((a, w) => a + w.totalSets, 0) + Object.keys(datos.progress).length],
    ["Ejercicios con historial", Object.keys(datos.history).length],
    ["Métricas con medidas", Object.keys(datos.measurements).length],
    ["Días entrenados", datos.trainingDates.length],
  ];
}
